import { Link } from "@tanstack/react-router";

type ToolIcon = "scan" | "ruler" | "wrench" | "gauge";

function ToolGlyph({ icon, className }: { icon: ToolIcon; className: string }) {
  const paths: Record<ToolIcon, string[]> = {
    scan: ["M3 7V5a2 2 0 0 1 2-2h2", "M17 3h2a2 2 0 0 1 2 2v2", "M21 17v2a2 2 0 0 1-2 2h-2", "M7 21H5a2 2 0 0 1-2-2v-2", "M7 12h10"],
    ruler: ["M21.3 8.7 8.7 21.3a2.4 2.4 0 0 1-3.4 0l-2.6-2.6a2.4 2.4 0 0 1 0-3.4L15.3 2.7a2.4 2.4 0 0 1 3.4 0l2.6 2.6a2.4 2.4 0 0 1 0 3.4Z", "m7.5 13.5 3 3", "m10.5 10.5 3 3", "m13.5 7.5 3 3"],
    wrench: ["M14.7 6.3a4 4 0 0 0-5-5l2.1 2.1-2.8 2.8-2.1-2.1a4 4 0 0 0 5 5l-7.8 7.8a2.1 2.1 0 0 0 3 3l7.8-7.8a4 4 0 0 0 5-5l-2.1 2.1-2.8-2.8 2.1-2.1Z"],
    gauge: ["M20.4 15a9 9 0 1 0-16.8 0", "M12 12l4-4", "M7 17h10"],
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {paths[icon].map((path) => <path key={path} d={path} />)}
    </svg>
  );
}

/**
 * Site-wide "next step" strip rendered above the footer on every page.
 * Every page therefore ends with a reason to click through rather than
 * a dead end, which lifts pages-per-session and ad viewability.
 */
const DESTINATIONS = [
  {
    eyebrow: "Most used",
    title: "Decode any VIN",
    body: "Engine, drivetrain & towing from a VIN",
    to: "/vin-decoder" as const,
    icon: "scan" as const,
  },
  {
    eyebrow: "Visual",
    title: "Compare car sizes",
    body: "Overlay two vehicles to scale",
    to: "/car-size-comparison" as const,
    search: { a: undefined, b: undefined },
    icon: "ruler" as const,
  },
  {
    eyebrow: "Ownership",
    title: "Maintenance schedule",
    body: "Service intervals + PDF export",
    to: "/car-maintenance-schedule" as const,
    icon: "wrench" as const,
  },
  {
    eyebrow: "Rankings",
    title: "Best MPG, ranked",
    body: "Top EPA combined MPG by class",
    to: "/best" as const,
    icon: "gauge" as const,
  },
];

export function KeepExploring() {
  return (
    <section
      aria-labelledby="keep-exploring-heading"
      className="border-t border-border section-band"
    >
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex items-baseline justify-between gap-4">
          <h2 id="keep-exploring-heading" className="text-base font-semibold text-foreground">
            Free tools people use next
          </h2>
          <span className="text-xs text-muted-foreground">No account needed</span>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {DESTINATIONS.map((d) => (
            <Link
              key={d.title}
              to={d.to}
              {...(d.search ? { search: d.search } : {})}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 transition-colors hover:border-primary/50 hover:bg-secondary/40"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <ToolGlyph icon={d.icon} className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-foreground group-hover:text-primary">
                  {d.title}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{d.body}</span>
              </span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}